import React from "react";
import { Hero, PageContent } from "../../components";

function EngineeringSuccess() {
  return (
    <div className="flex overflow-x-hidden overflow-y-hidden relative flex-col -z-10">
      <Hero title={"Engineering Success"}>
        Engineering is seeing obstacles as {" "}
        <span className="text-lg font-black text-RougePastel md:text-xl lg:text-2xl">
          puzzles
        </span>{" "}
        to be solved.
      </Hero>
      <PageContent>
        {`
Clean Heat is a full system able to provide heat to houses while depolluting wastewater from the endocrine disruptors 17-alpha-ethinylestradiols.  
Our aim was to develop as much as possible all the different parts of our project including the synthesis and characterization of our plasmids, 
but also the other points such as the co-culture or the waste recycling part.
For this, we have been through all the following engineering cycles:  

<div class="image_frame">
    <img class="illustration big" src="https://static.igem.wiki/teams/4577/wiki/project/engineering-success/growth-adaptation-to-wastewater-and-urine-1.jpg">
</div>


# Cycle 1: Growth adaptation to wastewater and urine media   
## Iteration 1:  
- **Design:** 
  Based on studies, culture media production is the second source of carbon emission of a micro-algae photobioreactor with 21 % of the emissions [1].
  Moreover, as culture media are mainly composed of water, they are resource consuming, a resource that is already rare in some parts of the globe 
  and that will become even more in the future decades. Indeed, currently, half the globe has already experienced water issues during a period of the year. 
  [2] Therefore, using waste as culture media is a good way to reduce the environmental impact of system and create a circular economy. 
  Through our research we discovered that *C. reinhardtii* and *S. meliloti* were supposed to grow in wastewater and that they even have natural properties to depollute them. 
  [3] As we have learnt during our Human Practices work, urine could also be an interesting waste to use to help in the revalorization cycle. However, 
  to meet our lab safety standards we couldn’t use real wastewater nor real urine for our tests. Thus, we designed experiments based on synthetic urine and synthetic wastewater.  
  We opted for a gradual adaptation to such media by increasing sequentially the proportion of synthetic urine /wastewater. The stages done were 25 %, 50%, 75% and 100%. 
  We entered on the next stage once the maximum cell density reached by the condition was at least equal to 80% of the one of the control.  

- **Build:** 
  Synthetic urine and synthetic wastewater media were obtained ([link to Experiments](https://2023.igem.wiki/ionis-paris/project/experiments)) 

- **Test:** 
  To evaluate the growth, microorganisms were seeded at an OD of 0.05 (OD600nm for *S. meliloti* and OD750nm for *C. reinhardtii* ) and OD measurement were done every day.  

- **Learn:** 
  Although both micro-organisms were able to grow in wastewater, it was not the case in urine. Indeed, while reaching 75% of synthetic urine, the micro-algae died.  
  We knew that some of the synthetic urine components are unstable (urea and creatine) over time, therefore we emitted the hypothesis this degradation could kill the microalgae.  

## Iteration 2:
- **Design & Build:** 
  A new synthetic urine was made in smaller amounts to reduce the storage time. Moreover, the solution was sterilized completely by filtration to avoid degradation during autoclave. 

- **Test:**
  New adaptation was started, and OD750nm measured every day. Cell death happened even earlier than during the previous test. The problem is not coming from a degradation of the media during storage.  

- **Learn:** 
  *C. reinhardtii* is not able to grow in 100% of urine. However, thanks to one of our Human Practices meeting with Mathieu Sperandio we learnt that a Belgium lab managed to cultivate microalgae in non-diluted urine by placing before the photobioreactor a batch of nitrifying inocula. [3] Therefore, the next engineering cycle to assess if would had the time and the equipments would have been to test the culture of *C. reinhardtii* in non-diluted urine after having cultivated *Nitrobacter spp* inside. [4] 

#  Cycle 2: Co-culture assay
## Iteration 1:
- **Design:**
  We started by looking at what has been done in terms of co-culture assay in the literature. As we knew that *C. reinhardtii*  have a lower growth in wastewater, we didn’t want to start the first try of culture in this media. The approach that seems to us to be the easiest for a first try was to use a mix of 50% of TAP and 50% of LB media.  

- **Build:**
  For this experiment *C. reinhardtii* and *S. meliloti* were both seeded at an OD of 0.1. 

- **Test:**
  Every three days, Malassez cell were used to count *C. reinhardtii* and a bacteria count on LB agar plates was done. Cells were passaged and seeded to have the same amount of *C. reinhardtii* than at the beginning.  

- **Learn:**
  The bacteria overgrew the microalgae in the co-culture, it is not sustainable over time. However, even in the control where microalgae were alone, they were not growing.  *C. reinhardtii* is not able to survive in 50% of TAP and 50% of LB media. We should try the co-culture in another media. 

## Iteration 2:
- **Design:** 
  As our final goal is to cultivate both microorganisms in wastewater, we decided to try the co-culture directly in wastewater.  

- **Build:**
  For this experiment, the following seeding condition with constant number of bacteria were tried:  
  - 80 000 *C. reinhardtii*/mL 

  - 100 000 *C. reinhardtii*/mL 

  - 200 000 *C. reinhardtii*/mL 

  - 400 000 *C. reinhardtii*/mL 

  - 1 000 000 *C. reinhardtii*/mL 

 

- **Test:**
  The same test as for the previous iteration was done. 

- **Learn:** 
  Again, the bacteria got the upper hand. The conditions tested were not viable for *C. reinhardtii*. The condition with the highest seeding density was the one who stayed the longest alive while the one with the lowest seeding density had the best growth rate at the beginning.  
  None of the conditions tried in wastewater were successful, with the culture condition we had in the lab a co-culture in wastewater wasn’t working. As the problem is coming from the microalgae, we need to find culture condition more optimum for it.  

## Iteration 3:
- **Design:** 
  The most optimal media for *C. reinhardtii* is TAP, therefore we tried the co-culture directly in TAP. 

- **Build:**
  For this experiment, the following seeding condition with constant number of bacteria were tried:  

  - 100 000 *C. reinhardtii*/mL 

  - 400 000 *C. reinhardtii*/mL 

- **Test:**
  The same test as for the previous iteration was done. 

- **Learn:** 
  Even in TAP the microalgae are dominated by the bacteria after few passages. In our lab, we weren’t able to change other parameters, such as the agitation or the lightwe didn’t have better condition to offer to *C. reinhardtii*.  As we didn’t find a solution to enhance *C. reinhardtii* growth in co-culture, a solution to our problem would be to supplement the culture with a microalgae inoculum when needed.  

## Iteration 4:
- **Design:**
  It was planned to use the condition with 400 000 *C. reinhardtii*/mL from the previous test and supplemented with a quarter of the original seeding density. Moreover, to be closer to photobioreactor growth condition we didn’t passage cell at always the same seeding density. Instead, cultures were centrifugated and fully resuspended in media. Cell concentration wasn’t decreased unless necessary.  

- **Build:**
  An inoculum of 2 500 000 cells was added to the co-culture condition 400 000 *C. reinhardtii*/mL in TAP. 

- **Test:** 
  Every three or four days, *C. reinhardtii* were counted and the co-culture centrifugated to be resuspended in fresh media.  

- **Learn:**
We never had to supplement the co-culture, a steady state between *C. reinhardtii* and *S. meliloti* was found. This method, similar to what is done with fed batch method in a photobioreactor, is a success. The next step would be to test the co-culture in similar conditions in wastewater.  

#  Cycle 3: Transformation of *S. meliloti* by electroporation
## Iteration 1:
- **Design:**
  For the EE2 inactivation we designed a plasmid composed of our two composite parts  BBa_K4577007 and BBa_K4577008, respectively responsible of *hao* and *amoa* gene expression.

- **Build:**
  Plasmid was ordered fully synthetized to IDT, then it was amplified by doing *E. coli dh5-alpha* transformation. A miniprep was done with NEB monarch miniprep kit plasmid to extract the plasmid from *E. coli*. Finally the plasmid was transferred to ***S. meliloti*** by electroporation.  
  After electroporation, cells were grown on plates with selective media with Kanamycin (50 mg/ml). 
  Cells that have grown were collected and plasmid extraction was done. To check if the DNA obtained was the correct one, an enzymatic digestion was performed.  
  The results of the digestion showed that bacteria didn’t contain our plasmid. 

- **Test:** 
  After electroporation, cells were grown on plates with selective media with Kanamycin (50 mg/ml). 
  Cells that have grown were collected and plasmid extraction was done. To check if the DNA obtained was the correct one, an enzymatic digestion was performed.  
  The results of the digestion showed that bacteria didn’t contain our plasmid. 

- **Learn:**
  ***S. meliloti*** is able to grow on media supplemented with Kanamycin (50 mg/ml) even though it didn’t have the resistance gene.  

## Iteration 2:
- **Design:** 
  By doing a test of growth on a standard range of antibiotics we determined that the required dose of kanamycin to avoid the growth of bacteria without plasmid was 250 mg/ml.  

- **Build:**
  Same plasmid and protocol than for the previous build phase. 

- **Test:**
  Same test than right before 

- **Learn:** 
  The bacterial mat has disappeared; however, no bacteria grew on the plate meaning the electroporation wasn’t successful. We managed to solve the problem of bacterial mat after electroporation, but more time would have been required to successfully transform the bacteria and find the correct parameters for electroporation with our square wave electroporator.  Another solution would have been to transform the bacteria by conjugation but as it wasn’t in our first plan we didn’t have material for it.  To check the correct expression of our plasmid we decided to transform *Bl21 E.coli* strain. 

# Cycle 4: Building of AOX plasmid for *C. reinhardtii*
## Iteration 1:
- **Design:**
  We designed two plasmids, each one containing a different version of our aox gene: AOX_3introns (BBa_K4577000) or AOX_1intron (BBa_K4577001). The vector we used was pMBS810 from Moclo Kit for *C. reinhardtii*. Our coding sequences have been associated with a strong promoter pPSAD (BBa_K4316025) and terminator tPSAD(BBa_K4316024).  

- **Build:**
  First the biobricks prefix and suffix were added respectively to our pPSAD promoter and tPSAD terminator by PCR. Then for the assembly we used the Golden Gate method with BsaI enzyme

- **Test:**
  The plasmid was amplified, extracted and digested by NdeI and SapI restriction enzymes. An electrophoresis was done to check the digestion’s result. 

- **Learn:**
  The fragments obtained after digestion were not the correct size. It wasn’t the correct plasmid. Another method of assembly was needed.  

## Iteration 2:
- **Design:** 
  To avoid self-assembly, we did separately the digestion of the elements and then the ligation. Two approaches were tried at the same time: 
    - All the digested elements were mixed for ligation, 
    - First, digested promoter, insert and terminator were ligated and then the product was ligated with the digested backbone. 

- **Build:**
  All parts were digested using BsaI enzyme, then only the part of interest of the backbone was kept by doing electrophoresis and gel purification. Either all the digested parts were mixed for ligation or only promoter, coding sequence and terminator were ligated and then ligated with the backbone.

- **Test:**
  We tried to amplify the assemblies in *E. coli* but it was a failure. The assembly reactions didn’t work. 

- **Learn:** 
  Doing such a method is DNA consuming and a lot of DNA is lost between steps. As it has failed, another assembly method was required. 

## Iteration 3:
- **Design:** 
  Golden Gate method is easier to perform than separate digestion and ligation. Increasing number of cycles during incubation can increase the efficiency of the Golden Gate. We elaborate a protocol with cycles.  

- **Build:**
  Instead of incubating 1h at 37°C and then 5 min at 60°C, we incubated 30 times one minute at 37°C, then  and one minute at 16°C and finally 5 min at 60°C.  

- **Test:**
  The plasmid was amplified, extracted and digested by NdeI and SapI restriction enzymes . An electrophoresis was done to check the digestion’s result. 

- **Learn:**
  After digestion, we had the correct fragments’ size. Our plasmid construction was a success.The plasmid is correct. 

## Sources :
1. Mata, Teresa M. et al. 2018. « Carbon footprint of microalgae production in photobioreactor ». Energy Procedia 153: 432‑37.
2. Krebs, Nina. 2022. « Fact Sheet - Food and Water ». SIXTH ASSESSMENT REPORT. Working Group II – Impacts, Adaptation and Vulnerability. Intergovernal panel on Climate change.
3. Kong, et al. 2010. "Culture of Microalgae Chlamydomonas reinhardtii in Wastewater for Biomass Feedstock Production. Applied Biochemistry and Biotechnology, 160(1), 9‑18.
4. Coppens, Joeri et al. 2016. « Nitrification and microalgae cultivation for two-stage biological nutrient valorization from source separated urine ». Bioresource Technology 211: 41‑50.

        `}
      </PageContent>
    </div>
  );
}

export default EngineeringSuccess;
